import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { FlaskConical, Droplets, Gauge, Wind, Beaker, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PreviewRibbon } from "@/components/cef/cefShared";

/* Concentrate Lab CEF — /cef/lab/:serverId?item=<flower key>
   Four production mini-games, scored server-side and gated by production skill:
   HASH — hold temp & pressure in the green bands at the same time
   SHATTER — pop the purge bubbles before the timer; sloppy purge = crumbles
   BUDDER — whip rhythm: keep the needle oscillating in the amber zone
 /* Concentrate Lab — /cef/lab/:serverId — press & refine mini-games.
   Shardglass — pop bubbles against the clock (bubble canvas)
   Goldwhip — keep the churn in the amber band
   Remedy Sap — time-gated reduction; start it and come back (highest value) */

const GAMES = {
  hash: { name: "Hash Press", icon: Gauge, skill: 0, hint: "Hold temp 155–165° and pressure 60–75% together for 6s" },
  shatter: { name: "Shardglass Set", icon: Droplets, skill: 3, hint: "Pop every bubble before the clock dies. Under 35 = crumble" },
  budder: { name: "Goldwhip Churn", icon: Wind, skill: 6, hint: "Keep the churn in the amber band — smooth strokes" },
  rso: { name: "Remedy Sap Reduction", icon: Beaker, skill: 14, hint: "Twenty minutes of slow reduction. Set it, don't watch it" },
};

export default function LabCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;
  const itemKey = params.get("item") || "product|white_widow|topshelf";

  const [kind, setKind] = useState("hash");
  const [job, setJob] = useState(null); // {id, ready_at}
  const [score, setScore] = useState(0);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  // hash: two drifting gauges, hold both in-band
  const [temp, setTemp] = useState(150);
  const [press, setPress] = useState(55);
  const [holdT, setHoldT] = useState(0);
  // shatter: bubbles
  const [bubbles, setBubbles] = useState([]);
  const [purgeLeft, setPurgeLeft] = useState(20);
  // budder: rhythm needle
  const [whip, setWhip] = useState(0);
  const whipHits = useRef(0); const whipTotal = useRef(0);

  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    return () => { document.documentElement.style.background = ""; document.body.style.background = ""; };
  }, []);

  const start = async () => {
    setBusy(true);
    if (demo) { setJob({ id: "demo", ready_at: new Date(Date.now() + (kind === "rso" ? 20000 : 0)).toISOString() }); setRunning(true); setBusy(false); beginGame(); return; }
    try {
      const r = await api.post(`/nc/servers/${serverId}/grow/concentrate/start`, { kind, item_key: itemKey, grams: 4 }, { params: { token } });
      setJob({ id: r.data.job, ready_at: r.data.ready_at });
      setRunning(true);
      beginGame();
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Can't start that run"); }
    finally { setBusy(false); }
  };

  const beginGame = () => {
    if (kind === "shatter") {
      setBubbles(Array.from({ length: 9 }, (_, i) => ({ id: i, x: 8 + Math.random() * 84, y: 12 + Math.random() * 76, s: 20 + Math.random() * 22, popped: false })));
      setPurgeLeft(20);
    }
    if (kind === "hash") { setHoldT(0); }
    whipHits.current = 0; whipTotal.current = 0;
  };

  // hash drift loop
  useEffect(() => {
    if (!running || kind !== "hash" || done) return undefined;
    const t = setInterval(() => {
      setTemp((v) => v + (Math.random() - 0.48) * 4);
      setPress((v) => v + (Math.random() - 0.5) * 5);
    }, 220);
    return () => clearInterval(t);
  }, [running, kind, done]);
  const inTemp = temp >= 155 && temp <= 165, inPress = press >= 60 && press <= 75;
  useEffect(() => {
    if (!running || kind !== "hash" || done) return undefined;
    const t = setInterval(() => {
      setHoldT((h) => {
        const next = inTemp && inPress ? h + 0.25 : Math.max(0, h - 0.5);
        if (next >= 6) { setScore(70 + Math.min(30, Math.round((160 - Math.abs(160 - temp)) * 2))); finishRef.current?.(70 + Math.min(30, Math.round(Math.random() * 25))); }
        return Math.min(6, next);
      });
    }, 250);
    return () => clearInterval(t);
  }, [running, kind, done, inTemp, inPress, temp]);

  // shatter countdown
  useEffect(() => {
    if (!running || kind !== "shatter" || done) return undefined;
    if (purgeLeft <= 0) { finishRef.current?.(Math.round((bubbles.filter((b) => b.popped).length / bubbles.length) * 100)); return undefined; }
    if (bubbles.length && bubbles.every((b) => b.popped)) { finishRef.current?.(100); return undefined; }
    const t = setTimeout(() => setPurgeLeft((p) => p - 1), 1000);
    return () => clearTimeout(t);
  }, [running, kind, purgeLeft, bubbles, done]);

  const finish = useCallback(async (finalScore) => {
    if (done || !job) return;
    setDone({ score: finalScore });
    setRunning(false);
    if (demo) { setDone({ score: finalScore, message: finalScore >= 35 ? `Run complete — ${(4 * 0.16 * (0.5 + finalScore / 100)).toFixed(2)}g out (preview)` : "It crumbled. (preview)" }); return; }
    try {
      const r = await api.post(`/nc/servers/${serverId}/grow/concentrate/finish`, { job: job.id, score: finalScore }, { params: { token } });
      setDone({ score: finalScore, message: r.data.message, ok: r.data.ok });
    } catch (e) { setDone({ score: finalScore, message: e.response?.data?.detail || "Finish failed", ok: false }); }
  }, [done, job, demo, serverId, token]);
  const finishRef = useRef(finish);
  finishRef.current = finish;

  // budder whip — pointer strokes
  const whipMove = (e) => {
    if (!running || kind !== "budder" || done) return;
    whipTotal.current += 1;
    if (whip >= 35 && whip <= 65) whipHits.current += 1;
    const r = e.currentTarget.getBoundingClientRect();
    setWhip(Math.round(((e.clientY - r.top) / r.height) * 100));
  };
  const finishWhip = () => finishRef.current?.(whipTotal.current ? Math.round((whipHits.current / whipTotal.current) * 100) : 0);

  // Remedy Sap — pure time gate
  const rsoReady = job && new Date(job.ready_at).getTime() <= Date.now();

  return (
    <div data-testid="lab-page" className="min-h-screen bg-transparent flex items-end justify-center pb-8 relative overflow-hidden">
      <style>{`@keyframes ncFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }`}</style>
      {demo && <PreviewRibbon what="concentrate lab" />}
      <div className="w-full max-w-xl border border-white/15 bg-black/85 backdrop-blur-md" style={{ animation: "ncFadeUp 0.3s ease both" }}>
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-white/10">
          <FlaskConical className="w-4 h-4 text-sky-400" />
          <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-400">Concentrate lab</p>
          <p className="ml-auto font-mono text-[8px] text-zinc-600 uppercase tracking-[0.15em]">4g flower in · skill-gated</p>
        </div>

        {!running && !done && (
          <div className="p-5">
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(GAMES).map(([k, g]) => (
                <button key={k} data-testid={`lab-game-${k}`} onClick={() => setKind(k)}
                  className={`border p-4 text-left transition-colors ${kind === k ? "border-sky-400/60 bg-sky-950/20" : "border-white/10 hover:border-white/30"}`}>
                  <g.icon className={`w-4 h-4 ${kind === k ? "text-sky-300" : "text-zinc-500"}`} />
                  <p className="font-mono text-[10px] font-bold text-zinc-100 mt-2">{g.name}</p>
                  <p className="font-mono text-[8px] text-zinc-500 mt-1 leading-relaxed">{g.hint}</p>
                  <p className="font-mono text-[7px] text-zinc-600 mt-1.5 uppercase tracking-[0.15em]">skill {g.skill}+</p>
                </button>
              ))}
            </div>
            <button data-testid="lab-start" disabled={busy} onClick={start}
              className="mt-4 w-full py-3.5 font-mono text-[11px] font-bold uppercase tracking-[0.25em] bg-sky-500 hover:bg-sky-400 text-black transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />} Start {GAMES[kind].name}
            </button>
          </div>
        )}

        {/* HASH — dual gauge hold */}
        {running && kind === "hash" && !done && (
          <div className="p-5" data-testid="lab-hash">
            <div className="flex justify-center gap-8 mb-4">
              {[["TEMP", temp, 155, 165, "°"], ["PRESS", press, 60, 75, "%"]].map(([label, v, lo, hi, unit]) => (
                <div key={label} className="text-center">
                  <p className={`font-mono font-bold text-3xl ${v >= lo && v <= hi ? "text-emerald-300" : "text-zinc-200"}`}>{Math.round(v)}{unit}</p>
                  <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{label} · hold {lo}–{hi}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-3 mb-3">
              <button data-testid="hash-temp-up" onClick={() => setTemp((v) => v + 3)} className="flex-1 py-3 border border-white/15 font-mono text-[9px] uppercase hover:border-white/40 transition-colors">Temp +</button>
              <button data-testid="hash-temp-down" onClick={() => setTemp((v) => v - 3)} className="flex-1 py-3 border border-white/15 font-mono text-[9px] uppercase hover:border-white/40 transition-colors">Temp −</button>
              <button data-testid="hash-press-up" onClick={() => setPress((v) => v + 3)} className="flex-1 py-3 border border-white/15 font-mono text-[9px] uppercase hover:border-white/40 transition-colors">Press +</button>
              <button data-testid="hash-press-down" onClick={() => setPress((v) => v - 3)} className="flex-1 py-3 border border-white/15 font-mono text-[9px] uppercase hover:border-white/40 transition-colors">Press −</button>
            </div>
            <div className="h-2.5 bg-black/60 border border-white/10"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${(holdT / 6) * 100}%` }} /></div>
            <p className="font-mono text-[8px] text-zinc-500 text-center mt-2 uppercase tracking-[0.2em]">{inTemp && inPress ? "holding…" : "both gauges in the green, together"}</p>
          </div>
        )}

        {/* SHATTER — bubble purge */}
        {running && kind === "shatter" && !done && (
          <div className="p-5" data-testid="lab-shatter">
            <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.2em] text-center mb-3">purge: <span className="text-amber-300 font-bold">{purgeLeft}s</span> · pop every bubble</p>
            <div className="relative h-52 rounded border border-amber-200/20 bg-gradient-to-b from-amber-900/20 to-amber-950/40 overflow-hidden">
              {bubbles.map((b) => !b.popped && (
                <button key={b.id} data-testid={`bubble-${b.id}`} onClick={() => setBubbles((bs) => bs.map((x) => x.id === b.id ? { ...x, popped: true } : x))}
                  className="absolute rounded-full border border-amber-200/50 bg-amber-100/10 hover:bg-amber-100/30 transition-colors"
                  style={{ left: `${b.x}%`, top: `${b.y}%`, width: b.s, height: b.s }} />
              ))}
            </div>
          </div>
        )}

        {/* BUDDER — whip rhythm */}
        {running && kind === "budder" && !done && (
          <div className="p-5" data-testid="lab-budder">
            <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.2em] text-center mb-3">whip inside the amber band — then finish</p>
            <div data-testid="whip-pad" onPointerMove={whipMove} className="relative h-52 rounded border border-white/10 bg-black/40 touch-none cursor-crosshair overflow-hidden">
              <div className="absolute inset-x-0 bg-amber-400/15 border-y border-amber-400/40" style={{ top: "35%", height: "30%" }} />
              <div className="absolute inset-x-0 h-0.5 bg-sky-300" style={{ top: `${whip}%` }} />
            </div>
            <button data-testid="budder-finish" onClick={finishWhip} className="mt-3 w-full py-3 font-mono text-[10px] font-bold uppercase tracking-[0.25em] bg-amber-400 text-black">Stop whipping</button>
          </div>
        )}

        {/* Remedy Sap — time gate */}
        {running && kind === "rso" && !done && (
          <div className="p-5 text-center" data-testid="lab-rso">
            <Beaker className="w-8 h-8 text-emerald-400 mx-auto animate-pulse" />
            <p className="font-mono text-[10px] text-zinc-300 mt-3">Slow extraction running…</p>
            <p className="font-mono text-[8px] text-zinc-500 mt-1 uppercase tracking-[0.2em]">{rsoReady ? "ready to finish" : `ready ${new Date(job?.ready_at).toLocaleTimeString()}`}</p>
            <button data-testid="rso-finish" disabled={!demo && !rsoReady} onClick={() => finishRef.current?.(85)}
              className="mt-4 px-8 py-3 font-mono text-[10px] font-bold uppercase tracking-[0.25em] bg-emerald-500 text-black disabled:opacity-30 transition-opacity">Decant &amp; finish</button>
          </div>
        )}

        {/* done */}
        {done && (
          <div className="p-6 text-center" data-testid="lab-result">
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-500">Run complete</p>
            <p className={`font-mono font-bold text-4xl mt-3 ${done.ok === false ? "text-red-400" : "text-sky-300"}`}>{done.score}<span className="text-base text-zinc-500">/100</span></p>
            <p className="font-mono text-[10px] text-zinc-300 mt-2">{done.message}</p>
            <button data-testid="lab-again" onClick={() => { setDone(null); setRunning(false); setJob(null); }}
              className="mt-4 px-6 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] border border-white/20 text-zinc-200 hover:border-white/50 transition-colors">Another run</button>
          </div>
        )}
      </div>
    </div>
  );
}
